/**
 * Поле даты с выпадающим календарём.
 *
 * Перенос календаря из присланного образца (ck-calendar): шапка
 * «год назад · месяц назад · месяц и год · месяц вперёд · год вперёд»,
 * неделя с понедельника, дни соседних месяцев приглушены, сегодняшний
 * подчёркнут, выбранный обведён. Цвета — токены приложения, а не
 * собственная палитра образца: так календарь сам следует теме.
 *
 * Значение — ISO-дата «ГГГГ-ММ-ДД» или пустая строка, как у прежнего
 * <input type="date">: вызывающий код менять не пришлось. Даты
 * считаются в UTC строками, без местного часового пояса: new Date("…")
 * западнее Гринвича дал бы предыдущий день (см. formatDate).
 *
 * С клавиатуры: стрелки двигают по дням и неделям, Home/End — к началу
 * и концу недели, PageUp/PageDown — на месяц (с Shift — на год),
 * Enter или пробел выбирают. Escape закрывает календарь.
 *
 * С typeable дату можно и напечатать «ДД.ММ.ГГГГ»: точки ставятся сами,
 * значение уходит наверх, как только набрана существующая дата в пределах
 * min/max. Недобранное или неверное при уходе с поля откатывается
 * к последнему принятому значению. Календарь открывает кнопка-значок.
 */

import { Popover as HuiPopover, PopoverButton, PopoverPanel } from "@headlessui/react";
import {
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
} from "lucide-react";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";

import { formatDate, todayIso } from "@/shared/lib/date";

import { cn } from "./cn";
import {
  addDays,
  addMonths,
  dayTitle,
  monthName,
  PANEL,
  parseIso,
  toIso,
  useTypedDate,
  WEEKDAYS,
} from "./dateParts";
import { SHEET_PANEL, useSheet } from "./sheet";

interface DateInputProps {
  /** ISO-дата «ГГГГ-ММ-ДД»; пустая строка — дата не выбрана. */
  value: string;
  /** Пустая строка — дату очистили. */
  onChange: (value: string) => void;
  /** Раньше этой даты выбрать нельзя. */
  min?: string | undefined;
  /** Позже этой даты выбрать нельзя. */
  max?: string | undefined;
  disabled?: boolean | undefined;
  invalid?: boolean | undefined;
  /** Крестик очистки рядом с выбранной датой. */
  clearable?: boolean;
  /** Дату можно напечатать «ДД.ММ.ГГГГ», а не только выбрать в календаре. */
  typeable?: boolean;
  placeholder?: string;
  id?: string | undefined;
  "aria-label"?: string | undefined;
  "aria-describedby"?: string | undefined;
  "aria-invalid"?: boolean | undefined;
  className?: string | undefined;
}

export function DateInput({
  value,
  onChange,
  min,
  max,
  disabled = false,
  invalid = false,
  clearable = true,
  typeable = false,
  placeholder = "Выбрать дату",
  id,
  "aria-label": ariaLabel,
  "aria-describedby": describedBy,
  "aria-invalid": ariaInvalid,
  className,
}: DateInputProps) {
  const sheet = useSheet();
  const showClear = clearable && value !== "" && !disabled;
  const typed = useTypedDate(value, onChange, min, max);

  const clearButton = showClear && (
    <button
      type="button"
      aria-label="Очистить дату"
      title="Очистить дату"
      onClick={() => onChange("")}
      className={cn(
        "absolute top-1/2 grid size-6 -translate-y-1/2 cursor-pointer place-items-center rounded-sm text-faint",
        typeable ? "right-2" : "right-8",
        "hover:bg-sunken hover:text-fg focus-visible:outline-2 focus-visible:outline-accent",
      )}
    >
      <X className="size-3.5" aria-hidden />
    </button>
  );

  const panel = (
    <PopoverPanel
      {...(sheet
        ? { portal: true, className: cn(PANEL, SHEET_PANEL, "p-3") }
        : { anchor: { to: "bottom start", gap: 6 } as const, className: cn(PANEL, "w-72") })}
    >
      {({ close }) => (
        <Calendar
          value={value}
          min={min}
          max={max}
          onSelect={(next) => {
            onChange(next);
            close();
          }}
        />
      )}
    </PopoverPanel>
  );

  if (typeable) {
    return (
      <div className={cn("relative", className)}>
        <HuiPopover>
          <div
            className={cn(
              "flex h-9 w-full items-center gap-1 rounded-md border bg-surface pl-1 text-sm transition-colors",
              "focus-within:border-accent",
              disabled && "cursor-not-allowed opacity-60",
              invalid || typed.invalid ? "border-danger" : "border-line",
              showClear ? "pr-9" : "pr-3",
            )}
          >
            <PopoverButton
              disabled={disabled}
              aria-label="Открыть календарь"
              title="Открыть календарь"
              className={cn(
                "grid size-7 shrink-0 cursor-pointer place-items-center rounded-sm text-faint",
                "hover:bg-sunken hover:text-fg focus-visible:outline-2 focus-visible:outline-accent data-open:text-accent",
              )}
            >
              <CalendarDays className="size-4" aria-hidden />
            </PopoverButton>
            <input
              id={id}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="ДД.ММ.ГГГГ"
              maxLength={10}
              disabled={disabled}
              aria-label={ariaLabel}
              aria-describedby={describedBy}
              aria-invalid={ariaInvalid ?? (typed.invalid || undefined)}
              data-value={value}
              value={typed.text}
              onChange={typed.change}
              onBlur={typed.blur}
              className="h-full min-w-0 flex-1 bg-transparent text-fg tabular-nums outline-none placeholder:text-faint disabled:cursor-not-allowed"
            />
          </div>
          {clearButton}
          {panel}
        </HuiPopover>
      </div>
    );
  }

  return (
    <div className={cn("relative", className)}>
      <HuiPopover>
        <PopoverButton
          id={id}
          disabled={disabled}
          // Значение — часть имени: подпись поля (label) у кнопки
          // заменяет её содержимое, и выбранная дата иначе не звучала бы.
          aria-label={
            ariaLabel === undefined
              ? undefined
              : `${ariaLabel}: ${value === "" ? "не выбрана" : formatDate(value)}`
          }
          aria-describedby={describedBy}
          aria-invalid={ariaInvalid}
          data-value={value}
          className={cn(
            "flex h-9 w-full cursor-pointer items-center gap-2 rounded-md border bg-surface px-3 text-left text-sm",
            "outline-none transition-colors disabled:cursor-not-allowed disabled:opacity-60",
            "focus-visible:border-accent data-open:border-accent",
            invalid ? "border-danger" : "border-line",
            showClear ? "pr-16" : "pr-8",
          )}
        >
          <CalendarDays className="size-4 shrink-0 text-faint" aria-hidden />
          <span className={cn("min-w-0 flex-1 truncate tabular-nums", value === "" ? "text-faint" : "text-fg")}>
            {value === "" ? placeholder : formatDate(value)}
          </span>
        </PopoverButton>
        <ChevronDown
          className="pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2 text-faint"
          aria-hidden
        />
        {clearButton}
        {panel}
      </HuiPopover>
    </div>
  );
}

interface CalendarProps {
  value: string;
  min?: string | undefined;
  max?: string | undefined;
  onSelect: (value: string) => void;
}

/** Месяц с днями. Отдельно от поля — пригодится и без выпадающей панели. */
export function Calendar({ value, min, max, onSelect }: CalendarProps) {
  const today = todayIso();
  // День под фокусом (roving tabindex); по нему же выбирается видимый месяц.
  const [focused, setFocused] = useState(() => (parseIso(value) === null ? today : value));
  // Фокус переносится на день только по клавиатуре и при открытии:
  // кнопки «месяц вперёд» и т. п. листают, не уводя фокус с себя.
  const moveFocus = useRef(true);
  const grid = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!moveFocus.current) return;
    moveFocus.current = false;
    grid.current?.querySelector<HTMLButtonElement>(`[data-date="${focused}"]`)?.focus();
  }, [focused]);

  const view = parseIso(focused) ?? new Date();
  const year = view.getUTCFullYear();
  const month = view.getUTCMonth();
  const first = new Date(Date.UTC(year, month, 1));
  // Неделя с понедельника: getUTCDay() даёт 0 для воскресенья.
  const offset = (first.getUTCDay() + 6) % 7;
  const count = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const cells = Math.ceil((offset + count) / 7) * 7;
  const days = Array.from({ length: cells }, (_, index) => {
    const date = new Date(Date.UTC(year, month, index - offset + 1));
    return { iso: toIso(date), day: date.getUTCDate(), outside: date.getUTCMonth() !== month, date };
  });

  const outOfRange = (iso: string) => (min !== undefined && iso < min) || (max !== undefined && iso > max);

  const title = monthName(first);

  const onDayKey = (event: KeyboardEvent<HTMLButtonElement>, iso: string) => {
    const date = parseIso(iso);
    if (date === null) return;
    const weekday = (date.getUTCDay() + 6) % 7;
    const shifts: Record<string, () => string> = {
      ArrowLeft: () => addDays(iso, -1),
      ArrowRight: () => addDays(iso, 1),
      ArrowUp: () => addDays(iso, -7),
      ArrowDown: () => addDays(iso, 7),
      Home: () => addDays(iso, -weekday),
      End: () => addDays(iso, 6 - weekday),
      PageUp: () => addMonths(iso, event.shiftKey ? -12 : -1),
      PageDown: () => addMonths(iso, event.shiftKey ? 12 : 1),
    };
    const shift = shifts[event.key];
    if (shift === undefined) return;
    event.preventDefault();
    moveFocus.current = true;
    setFocused(shift());
  };

  const navButton =
    "grid h-8 cursor-pointer place-items-center rounded-sm text-muted hover:bg-sunken hover:text-fg " +
    "focus-visible:outline-2 focus-visible:outline-accent";

  return (
    <div className="select-none">
      <div className="mb-2 grid grid-cols-[1.75rem_1.75rem_1fr_1.75rem_1.75rem] items-center gap-px">
        <button type="button" className={navButton} aria-label="Предыдущий год" onClick={() => setFocused(addMonths(focused, -12))}>
          <ChevronsLeft className="size-4" aria-hidden />
        </button>
        <button type="button" className={navButton} aria-label="Предыдущий месяц" onClick={() => setFocused(addMonths(focused, -1))}>
          <ChevronLeft className="size-4" aria-hidden />
        </button>
        <strong className="text-center text-sm font-bold text-fg" aria-live="polite">
          {title}
        </strong>
        <button type="button" className={navButton} aria-label="Следующий месяц" onClick={() => setFocused(addMonths(focused, 1))}>
          <ChevronRight className="size-4" aria-hidden />
        </button>
        <button type="button" className={navButton} aria-label="Следующий год" onClick={() => setFocused(addMonths(focused, 12))}>
          <ChevronsRight className="size-4" aria-hidden />
        </button>
      </div>

      <div className="mb-1 grid grid-cols-7 gap-0.5 text-center text-xs text-muted" aria-hidden>
        {WEEKDAYS.map((name) => (
          <span key={name} className="py-1">
            {name}
          </span>
        ))}
      </div>

      <div ref={grid} role="group" aria-label="Даты месяца" className="grid grid-cols-7 gap-0.5">
        {days.map(({ iso, day, outside, date }) => {
          const selected = iso === value;
          const blocked = outOfRange(iso);
          return (
            <button
              key={iso}
              type="button"
              data-date={iso}
              tabIndex={iso === focused ? 0 : -1}
              aria-label={dayTitle.format(date)}
              aria-pressed={selected}
              // aria-disabled, а не disabled: недоступный день остаётся
              // в обходе стрелками, иначе фокус на нём терялся бы.
              aria-disabled={blocked || undefined}
              {...(iso === today ? { "aria-current": "date" as const } : {})}
              onClick={() => {
                if (blocked) return;
                onSelect(iso);
              }}
              onKeyDown={(event) => onDayKey(event, iso)}
              className={cn(
                "min-h-8 cursor-pointer rounded-sm p-0.5 text-sm tabular-nums transition-colors",
                "hover:bg-sunken focus-visible:outline-2 focus-visible:outline-accent",
                outside ? "text-faint" : "text-fg",
                selected && "bg-accent-soft font-bold text-fg ring-1 ring-accent ring-inset",
                iso === today && !selected && "underline underline-offset-4",
                blocked && "cursor-not-allowed opacity-40 hover:bg-transparent",
              )}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}
